import React, { useEffect, useMemo, useState } from 'react';
import { Users, Filter, RefreshCw } from 'lucide-react';

const formatNumber = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString();
};

const DEFAULT_SEGMENTS = [
  {
    id: 'sales',
    title: 'Sales Behaviors',
    color: 'indigo',
    segments: [
      { key: 'no_orders_last_3m', name: 'Inactive (Last 3 Months)', desc: 'No orders in the last 3 months', count: 0 },
      { key: 'no_orders_last_6m', name: 'Inactive (Last 6 Months)', desc: 'No orders in the last 6 months', count: 0 },
      { key: 'exactly3_in_year_no_last3', name: '3 Orders This Year, Inactive 3M', desc: 'Exactly 3 orders this calendar year, none in last 3 months', count: 0 },
      { key: 'gt5_in_year', name: 'Frequent Buyers (5+ / Year)', desc: 'More than 5 orders this calendar year', count: 0 },
      { key: 'gt4_in_year', name: 'Active Buyers (4+ / Year)', desc: 'More than 4 orders this calendar year', count: 0 },
      { key: 'orders_ge_10_in_year', name: 'Very Frequent (10+ / Year)', desc: 'At least 10 orders this calendar year', count: 0 },
      { key: 'value_40k_in_year', name: 'High Value (₺40K+ / Year)', desc: 'Revenue ≥ ₺40,000 this calendar year', count: 0 },
      { key: 'revenue_100k_in_year', name: 'Elite Value (₺100K+ / Year)', desc: 'Revenue ≥ ₺100,000 this calendar year', count: 0 },
      { key: 'items_per_order_ge_3_in_year', name: 'Bulk Buyers (≥3 items/order)', desc: 'Average items/order ≥ 3 this year', count: 0 },
      { key: 'every_month_in_year', name: 'Monthly Loyal (Every Month)', desc: 'Has ordered in every month of this calendar year (to date)', count: 0 },
      { key: 'months_ge_9_in_year', name: 'Very Consistent (9+ months)', desc: 'Has orders in ≥ 9 months this year', count: 0 },
      { key: 'half_months_in_year', name: 'Consistent (≥ Half of Months)', desc: 'Has orders in at least half of months this year (to date)', count: 0 },
      { key: 'months_eq_1_in_year', name: 'Single-Month Buyers', desc: 'Ordered in only 1 month this year', count: 0 },
      { key: 'one_order_last_30d', name: 'Recent Single-Order (30d)', desc: 'Exactly 1 order in the current month', count: 0 },
      { key: 'ge2_orders_last_60d', name: '2+ Orders (Last 60d)', desc: 'Sum of orders in current and previous month ≥ 2', count: 0 }
    ]
  }
];

export default function WooCustomSegments() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  const [monthlyRows, setMonthlyRows] = useState([]);
  const [countsByKey, setCountsByKey] = useState({});
  const [idsByKey, setIdsByKey] = useState({});
  const [openSegments, setOpenSegments] = useState({});

  const categories = useMemo(() => DEFAULT_SEGMENTS, []);

  const computedCategories = useMemo(() => {
    return categories.map(cat => ({
      ...cat,
      segments: cat.segments.map(s => ({ ...s, count: countsByKey[s.key] || 0 }))
    }));
  }, [categories, countsByKey]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cats = category === 'all' ? computedCategories : computedCategories.filter(c => c.id === category);
    return cats.map(cat => ({
      ...cat,
      segments: cat.segments.filter(s => !q || s.name.toLowerCase().includes(q) || (s.desc || '').toLowerCase().includes(q))
    })).filter(cat => cat.segments.length > 0);
  }, [computedCategories, category, query]);

  const fetchMonthlyCustomers = async () => {
    try {
      setLoading(true);
      const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
      if (!brandId) { setMonthlyRows([]); setCountsByKey({}); return; }
      const headers = { 'X-Brand-ID': brandId };
      const base = (typeof window !== 'undefined' && window.location.hostname === 'localhost') ? '' : 'https://api.glowytics.com';
      const res = await fetch(`${base}/api/woocommerce/customers/monthly`, { credentials: 'include', headers });
      const js = await res.json().catch(() => ({}));
      const rows = res.ok && js?.rows ? js.rows : [];
      setMonthlyRows(rows);
      const { counts, ids } = computeSegments(rows);
      setCountsByKey(counts);
      setIdsByKey(ids);
    } catch (e) {
      console.error('fetchMonthlyCustomers error', e);
      setMonthlyRows([]);
      setCountsByKey({});
      setIdsByKey({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonthlyCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function ymKeyFromDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }

  function computeSegments(rows) {
    const now = new Date();
    const currentYear = now.getFullYear();
    const monthsSoFar = [];
    for (let i = 0; i <= now.getMonth(); i++) {
      monthsSoFar.push(`${currentYear}-${String(i + 1).padStart(2, '0')}`);
    }
    const last3 = [];
    for (let i = 0; i < 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      last3.push(ymKeyFromDate(d));
    }
    const last6 = [];
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      last6.push(ymKeyFromDate(d));
    }
    const last2 = [];
    for (let i = 0; i < 2; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      last2.push(ymKeyFromDate(d));
    }
    const currentMonthKey = ymKeyFromDate(new Date(now.getFullYear(), now.getMonth(), 1));

    const perCustomer = new Map();
    for (const r of (rows || [])) {
      if (!r || !r.customer_id || !r.month) continue;
      const m = typeof r.month === 'string' ? r.month.slice(0, 7) : ymKeyFromDate(new Date(r.month));
      const cust = r.customer_id;
      if (!perCustomer.has(cust)) {
        perCustomer.set(cust, {
          months: new Map(),
          yearOrders: 0,
          yearRevenue: 0,
          monthsWithOrdersInYear: new Set()
        });
      }
      const acc = perCustomer.get(cust);
      const prev = acc.months.get(m) || { orders: 0, items: 0, revenue: 0 };
      const orders = Number(r.orders_count || 0);
      const items = Number(r.items_count || 0);
      const rev = Number(r.revenue || 0);
      acc.months.set(m, {
        orders: prev.orders + orders,
        items: prev.items + items,
        revenue: prev.revenue + rev
      });
      const md = new Date(r.month);
      const y = md.getFullYear();
      if (y === currentYear) {
        acc.yearOrders += orders;
        acc.yearRevenue += rev;
        if (orders > 0) acc.monthsWithOrdersInYear.add(m);
      }
    }

    const counts = {
      no_orders_last_3m: 0,
      no_orders_last_6m: 0,
      exactly3_in_year_no_last3: 0,
      gt5_in_year: 0,
      gt4_in_year: 0,
      orders_ge_10_in_year: 0,
      value_40k_in_year: 0,
      revenue_100k_in_year: 0,
      items_per_order_ge_3_in_year: 0,
      every_month_in_year: 0,
      months_ge_9_in_year: 0,
      half_months_in_year: 0,
      months_eq_1_in_year: 0,
      one_order_last_30d: 0,
      ge2_orders_last_60d: 0
    };
    const ids = Object.fromEntries(Object.keys(counts).map(k => [k, []]));

    for (const [custId, acc] of perCustomer.entries()) {
      const yearOrders = acc.yearOrders;
      const yearRevenue = acc.yearRevenue;
      const itemsTotal = Array.from(acc.months.values()).reduce((s, v) => s + v.items, 0);
      const itemsPerOrder = yearOrders > 0 ? (itemsTotal / yearOrders) : 0;
      const monthsCountInYear = acc.monthsWithOrdersInYear.size;

      let last3Orders = 0;
      for (const k of last3) last3Orders += (acc.months.get(k)?.orders || 0);
      if (last3Orders === 0) { counts.no_orders_last_3m++; ids.no_orders_last_3m.push(custId); }

      let last6Orders = 0;
      for (const k of last6) last6Orders += (acc.months.get(k)?.orders || 0);
      if (last6Orders === 0) { counts.no_orders_last_6m++; ids.no_orders_last_6m.push(custId); }

      if (yearOrders === 3 && last3Orders === 0) { counts.exactly3_in_year_no_last3++; ids.exactly3_in_year_no_last3.push(custId); }
      if (yearOrders > 5) { counts.gt5_in_year++; ids.gt5_in_year.push(custId); }
      if (yearOrders > 4) { counts.gt4_in_year++; ids.gt4_in_year.push(custId); }
      if (yearOrders >= 10) { counts.orders_ge_10_in_year++; ids.orders_ge_10_in_year.push(custId); }
      if (yearRevenue >= 40000) { counts.value_40k_in_year++; ids.value_40k_in_year.push(custId); }
      if (yearRevenue >= 100000) { counts.revenue_100k_in_year++; ids.revenue_100k_in_year.push(custId); }
      if (itemsPerOrder >= 3) { counts.items_per_order_ge_3_in_year++; ids.items_per_order_ge_3_in_year.push(custId); }

      const allMonthsHaveOrders = monthsSoFar.every(k => (acc.months.get(k)?.orders || 0) > 0);
      if (allMonthsHaveOrders && monthsSoFar.length > 0) { counts.every_month_in_year++; ids.every_month_in_year.push(custId); }

      if (monthsCountInYear >= 9) { counts.months_ge_9_in_year++; ids.months_ge_9_in_year.push(custId); }

      const halfThreshold = Math.ceil(monthsSoFar.length / 2);
      if (monthsCountInYear >= halfThreshold) { counts.half_months_in_year++; ids.half_months_in_year.push(custId); }
      if (monthsCountInYear === 1) { counts.months_eq_1_in_year++; ids.months_eq_1_in_year.push(custId); }

      const currentMonthOrders = acc.months.get(currentMonthKey)?.orders || 0;
      if (currentMonthOrders === 1) { counts.one_order_last_30d++; ids.one_order_last_30d.push(custId); }

      let last2Orders = 0;
      for (const k of last2) last2Orders += (acc.months.get(k)?.orders || 0);
      if (last2Orders >= 2) { counts.ge2_orders_last_60d++; ids.ge2_orders_last_60d.push(custId); }
    }

    for (const k of Object.keys(ids)) {
      ids[k] = Array.from(new Set(ids[k].filter(Boolean)));
    }

    return { counts, ids };
  }

  function toggleSegmentOpen(key) {
    setOpenSegments(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function exportIdsCsv(key) {
    const ids = idsByKey[key] || [];
    const rows = ['customer_id'];
    for (const id of ids) rows.push(`${id}`);
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${key}_customers.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 relative">
      {loading ? (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="flex items-center gap-3 text-gray-700">
            <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading segments...</span>
          </div>
        </div>
      ) : null}
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/woocommercelogo.webp" alt="WooCommerce" className="h-8 object-contain" />
            <h1 className="text-xl font-bold text-gray-900">Customer Segments</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2">
              <label className="text-xs text-gray-500">Category</label>
              <select
                className="text-xs border rounded-md px-2 py-1 bg-white"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="all">All</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="Search segments..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="text-sm border rounded-md px-3 py-1.5 pl-8 w-56 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <Filter size={14} className="absolute left-2 top-2.5 text-gray-400" />
            </div>
            <button
              className={`p-2 text-gray-600 rounded-xl hover:bg-gray-100 transition-colors ${loading ? 'opacity-50' : ''}`}
              onClick={fetchMonthlyCustomers}
              title="Refresh"
            >
              <RefreshCw size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Segment categories */}
      {filtered.length === 0 ? (
        <div className="bg-white border rounded-2xl p-6 text-sm text-gray-500">No segments match your filters.</div>
      ) : filtered.map(cat => (
        <div key={cat.id} className="bg-white border rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">{cat.title}</h2>
            <span className="text-xs text-gray-500">{cat.segments.length} segments</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {cat.segments.map(seg => (
              <div key={seg.key} className="border rounded-xl p-3 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-gray-900">{seg.name}</div>
                    <div className="text-xs text-gray-500">{seg.desc}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      className={`text-xs px-2 py-0.5 rounded-md border bg-white hover:bg-gray-50`}
                      onClick={() => toggleSegmentOpen(seg.key)}
                    >
                      {openSegments[seg.key] ? 'Hide Users' : 'View Users'}
                    </button>
                    <button
                      className={`text-xs px-2 py-0.5 rounded-md border bg-white hover:bg-gray-50`}
                      onClick={() => exportIdsCsv(seg.key)}
                    >
                      Export CSV
                    </button>
                    <span className={`text-xs px-2 py-0.5 rounded-full bg-${cat.color}-50 text-${cat.color}-700 border border-${cat.color}-200`}>
                      Computed
                    </span>
                  </div>
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <div className="text-2xl font-bold text-gray-800">{formatNumber(seg.count)}</div>
                  <div className="h-2 flex-1 ml-3 rounded-full bg-gray-100 overflow-hidden">
                    <div className={`h-full bg-${cat.color}-500`} style={{ width: `${Math.min(100, Math.max(8, Math.round((seg.count % 10000) / 100)))}%` }} />
                  </div>
                </div>
                {openSegments[seg.key] ? (
                  <div className="mt-3 border-t pt-2">
                    <div className="text-xs text-gray-500 mb-1">User IDs ({(idsByKey[seg.key] || []).length})</div>
                    <div className="max-h-40 overflow-y-auto bg-gray-50 rounded-md p-2 text-xs">
                      {(idsByKey[seg.key] || []).length === 0 ? (
                        <div className="text-gray-400">No users in this segment.</div>
                      ) : (
                        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1">
                          {(idsByKey[seg.key] || []).map(id => (
                            <li key={`${seg.key}-${id}`} className="text-gray-700 break-all">{id}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


